import { DropdownMenu } from "radix-ui";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Icon } from "../icons";
import styles from "./Menu.module.css";
import { cx } from "../lib/cx";

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;

export type MenuContentProps = {
  readonly align?: "center" | "end" | "start";
  readonly children: ReactNode;
  readonly className?: string;
  readonly side?: "bottom" | "left" | "right" | "top";
  readonly sideOffset?: number;
};

export const MenuContent = ({
  align = "start",
  children,
  className,
  side = "bottom",
  sideOffset = 6,
}: MenuContentProps) => (
  <DropdownMenu.Portal>
    <DropdownMenu.Content
      align={align}
      className={cx(styles.content, "u-pop", className)}
      side={side}
      sideOffset={sideOffset}
    >
      {children}
    </DropdownMenu.Content>
  </DropdownMenu.Portal>
);

export type MenuItemProps = {
  readonly active?: boolean;
  readonly children: ReactNode;
  readonly className?: string;
  // A menu entry that navigates renders as an anchor so it can be opened in a
  // new tab like any other link (frontend.md §Hard rules).
  readonly href?: string;
  readonly onSelect?: () => void;
};

export const MenuItem = ({
  active = false,
  children,
  className,
  href,
  onSelect,
}: MenuItemProps) => {
  const itemClass = cx(styles.item, active && styles.active, className);
  if (href !== undefined) {
    return (
      <DropdownMenu.Item asChild className={itemClass} onSelect={onSelect}>
        <Link to={href}>{children}</Link>
      </DropdownMenu.Item>
    );
  }
  return (
    <DropdownMenu.Item className={itemClass} onSelect={onSelect}>
      {children}
    </DropdownMenu.Item>
  );
};

export const MenuGroupLabel = ({
  children,
}: {
  readonly children: ReactNode;
}) => <div className={styles.group}>{children}</div>;

export const MenuHint = ({ children }: { readonly children: ReactNode }) => (
  <span className={styles.hint}>{children}</span>
);

export const MenuCheck = () => (
  <Icon.Check className={styles.check} size={13} />
);
